/**
 * The colour an appliance card wears, and whether it breathes.
 *
 * A card that lights up the same grey for an oven, a washing machine and an
 * access point says nothing at a glance — the owner's note (25.10.2026) was
 * exactly that: *"when running the gray background is not visible breathing…
 * cooktop, oven, microwave.. brighter red, orange.. other devices like aps,
 * computers green no breathing"*.
 *
 * Two rules, and they are different questions:
 *  - **colour** is what the machine *is* — heat is red and orange, laundry is
 *    blue, computing is green, a valve is the water it carries;
 *  - **breathing** is whether it is *doing something* — a hob, an oven, a wash
 *    or a robot breathes while it works; a computer, a switch or a NAS is
 *    either up or down and holds still. An open valve breathes because it is a
 *    warning: water is running through a pipe somebody left open.
 */
import { switchKindOf } from '../components/devices/deviceWidgetTypes';

export type ApplianceToneName =
  | 'heat'
  | 'oven'
  | 'microwave'
  | 'coffee'
  | 'laundry'
  | 'cooling'
  | 'robot'
  | 'compute'
  | 'plug'
  | 'valve'
  | 'default';

export interface ApplianceCardTone {
  tone: ApplianceToneName;
  /** The accent mixed into the card's fill while the device is lit. */
  accent: string;
  /** A machine at work breathes; a device that only reports stays still. */
  breathing: boolean;
}

const TONES: Record<ApplianceToneName, ApplianceCardTone> = {
  // A hob is the hottest thing on the page: full red.
  heat: { tone: 'heat', accent: 'rgb(239 68 68)', breathing: true },
  oven: { tone: 'oven', accent: 'rgb(249 115 22)', breathing: true },
  microwave: { tone: 'microwave', accent: 'rgb(245 158 11)', breathing: true },
  coffee: { tone: 'coffee', accent: 'rgb(217 119 6)', breathing: true },
  // Laundry runs a cycle: water blue, and it breathes while the drum turns.
  laundry: { tone: 'laundry', accent: 'rgb(14 165 233)', breathing: true },
  // A fridge is on for years at a time. It is cold, and it is not "working".
  cooling: { tone: 'cooling', accent: 'rgb(56 189 248)', breathing: false },
  robot: { tone: 'robot', accent: 'rgb(139 92 246)', breathing: true },
  // The house's computing: up is the interesting state, and it holds still.
  compute: { tone: 'compute', accent: 'rgb(34 197 94)', breathing: false },
  // A plug is a load with no name of its own — the toaster, the tree lights.
  // While it is on it is doing something, so it breathes in the switch yellow.
  plug: { tone: 'plug', accent: 'rgb(250 204 21)', breathing: true },
  // A valve is water, and an open one is the house's warning light: it breathes.
  valve: { tone: 'valve', accent: 'rgb(56 189 248)', breathing: true },
  default: { tone: 'default', accent: 'rgb(148 163 184)', breathing: false },
};

/** The device fields the tone is read from — the same shape `switchKindOf` takes. */
export interface ApplianceToneInput {
  deviceType?: string | null;
  deviceControl?: string | null;
  controlType?: string | null;
  icon?: string | null;
  deviceName?: string | null;
  shortName?: string | null;
  connectedLoadName?: string | null;
}

/** Which tone a device's own type names — `null` when it names none. */
function toneFromText(text: string): ApplianceToneName | null {
  if (!text) return null;
  // Computing first: an access point called "Kitchen AP" is not a hob.
  if (/unifi_|synology_|\bnetwork\b|access_point|\bgateway\b|computer|server|\bnas\b|docker|hypervisor/.test(text)) {
    return 'compute';
  }
  if (/vacuum|mower|\brobot/.test(text)) return 'robot';
  if (/washer|dryer|dishwash|laundry/.test(text)) return 'laundry';
  if (/cooktop|hob\b|cook_surface|cooksurface|stove|stovetop/.test(text)) return 'heat';
  if (/microwave/.test(text)) return 'microwave';
  if (/oven|\bgrill/.test(text)) return 'oven';
  if (/coffee|kettle|espresso/.test(text)) return 'coffee';
  if (/refriger|fridge|freezer|wine_?cabinet|cooler/.test(text)) return 'cooling';
  return null;
}

/**
 * The card's tone for a device. Its type decides — `deviceType`, then the
 * control class, then the control type — and only a device that names none of
 * them falls back to the appliance drawing its tile carries (a socket whose
 * connected load is "Coffee machine" is a coffee machine).
 */
export function applianceCardTone(device: ApplianceToneInput): ApplianceCardTone {
  const direct = toneFromText(
    [device.deviceType, device.deviceControl, device.controlType]
      .map((part) => String(part ?? '').toLowerCase())
      .join(' '),
  );
  if (direct) return TONES[direct];

  const kind = switchKindOf(device);
  switch (kind) {
    case 'coffee':
      return TONES.coffee;
    case 'washer':
    case 'dryer':
    case 'dishwasher':
      return TONES.laundry;
    case 'fridge':
      return TONES.cooling;
    case 'oven':
      // The art folds the microwave, the oven and the hob into one drawing.
      return TONES.oven;
    case 'kettle':
      return TONES.coffee;
    case 'vacuum':
      return TONES.robot;
    case 'mower':
      return TONES.robot;
    case 'socket':
    case 'dimmer':
    case 'pump':
    case 'ev':
      return TONES.plug;
    case 'valve':
      return TONES.valve;
    default:
      return TONES.default;
  }
}
